import { useState } from 'react';

import { DateRangePicker, Text, dialogService } from '@thefarmersfront/kitchen';

import dayjs from 'dayjs';

import { useGetDashboardDeliveryTrendQuery } from '~/hooks/delivery-agency/queries';
import { formatDate } from '~/utils/date';

import { DeliveryAgencyDeliveryStatusTrendChart } from '../DeliveryAgencyDeliveryStatusTrendChart';

import type { TypeInputStatus } from '~/types/common.types';

import styles from '../DeliveryAgencyDeliveryStatusBoard.module.scss';

const MAX_SEARCH_RANGE_DAYS = 31;

const getDefaultEndDate = (): Date => {
  return dayjs().subtract(1, 'day').startOf('day').toDate();
};

const getDefaultStartDate = (): Date => {
  return dayjs(getDefaultEndDate())
    .subtract(MAX_SEARCH_RANGE_DAYS - 1, 'day')
    .startOf('day')
    .toDate();
};

const DeliveryAgencyDeliveryStatusTrend = () => {
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState<{ start: Date; end: Date }>({
    start: getDefaultStartDate(),
    end: getDefaultEndDate(),
  });
  const [datePickerStatus, setDatePickerStatus] = useState<TypeInputStatus>('default');

  const { data } = useGetDashboardDeliveryTrendQuery({
    params: {
      fromDeliveryDate: formatDate(estimatedDeliveryDate.start, 'YYYY-MM-DD') ?? '',
      toDeliveryDate: formatDate(estimatedDeliveryDate.end, 'YYYY-MM-DD') ?? '',
    },
  });

  const handleChange = (value: { start: Date; end: Date } | null) => {
    if (!value) return;

    const diffDays = dayjs(value.end).diff(dayjs(value.start), 'day');
    if (diffDays >= MAX_SEARCH_RANGE_DAYS) {
      setDatePickerStatus('error');
      dialogService.addDialog({
        children: `최대 ${MAX_SEARCH_RANGE_DAYS}일 기간으로 입력 후 검색해 주세요.`,
      });
      return;
    }

    setEstimatedDeliveryDate(value);
  };

  return (
    <section>
      <div className={styles['section-header']}>
        <Text size="xxlarge" weight="bold">
          배송 추이
        </Text>

        <div style={{ width: 228 }}>
          <DateRangePicker
            size="medium"
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={dayjs().subtract(1, 'day').startOf('day').toDate()}
            value={estimatedDeliveryDate}
            status={datePickerStatus}
            onChange={handleChange}
            onFocus={() => setDatePickerStatus('default')}
          />
        </div>
      </div>

      <DeliveryAgencyDeliveryStatusTrendChart data={data?.data?.dailyTrends ?? []} />
    </section>
  );
};

export { DeliveryAgencyDeliveryStatusTrend };
